import React, { useState } from 'react'
import Text from 'core-system/Text/Text'
import palette from 'core-system/Themes/palette'
import styled from 'styled-components'
import Checkbox from 'core-system/Checkbox/Checkbox'
import { LaunchProgramsType } from 'features/Programs/Launch/launchProgramsSlice'
import HelpIcon from '@mui/icons-material/Help'
import { Tooltip } from '@mui/material'
import Modal from 'core-system/Modal'
import CloseIcon from 'core-system/Icons/Actions/Close'
import { tooltipClasses, TooltipProps } from '@mui/material/Tooltip'
const CheckboxContainer = styled(Checkbox)`
  display: block;
  margin-top: 0.2rem;
`

const ModalContainer = styled(Modal)`
  max-width: 20rem;
  position: relative;
`

const ModelInnerContainer = styled.div`
  padding: 2rem 3rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100%;
  text-align: center;
`
const ProgramContainer = styled.div<{ disabled: boolean }>`
  display: flex;
  position: relative;
  padding: 1.5rem;
  border: 1px solid ${palette.grey.grey300};
  border-radius: 0.5rem;
  margin-bottom: 1rem;
  cursor: ${({ disabled }) => (disabled ? 'default' : 'pointer')};
  transition: background-color 0.3s ease;
  ${({ disabled }) =>
    !disabled &&
    `
    &:hover {
      background-color: ${palette.secondary.purple100};
      ${CheckboxContainer} {
        & > div {
          border-color: ${palette.secondary.purple600} !important;
        }
        & * svg {
          display: block;
          opacity: 1;
        }
      }
    }
  `}
`

const Close = styled.div`
  position: absolute;
  top: 0.4rem;
  right: 0.4rem;
  width: 2rem;
  height: 2rem;
  border-radius: 0.5rem;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;

  &:hover {
    background-color: ${palette.primary.pink200};
  }
`

const TitleContainer = styled.div`
  margin-left: 1rem;
`

const HelpIconContainer = styled.div`
  cursor: pointer;
  position: absolute;
  right: 12px;
  top: 12px;

  &:hover {
    color: ${palette.secondary.purple600};
  }
`

const TitleIconContainer = styled.div<{ checked: boolean; disabled: boolean }>`
  display: flex;
  gap: 0.75rem;

  & > svg {
    color: ${({ checked, disabled }) =>
      disabled
        ? palette.text.disabled
        : checked
          ? palette.secondary.purple600
          : palette.text.primary};
  }
`

const LightTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(() => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: palette.secondary.purple100,
    fontSize: '0.9rem',
    fontFamily: 'PolySans',
    color: palette.text.primary,
    border: `1px solid ${palette.secondary.purple300}`,
    borderRadius: '0.5rem',
    padding: '0.5rem 1rem',
  },
}))

interface ProgramSelectItemProps {
  id: LaunchProgramsType
  title: string
  subtitle: string
  onSelect: (program: LaunchProgramsType) => void
  checked: boolean
  disabled?: boolean
  disabledReason?: string
  icon: React.ReactNode
}

const ProgramSelectItem = ({
  id,
  title,
  subtitle,
  onSelect,
  checked,
  disabled,
  disabledReason,
  icon,
}: ProgramSelectItemProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const textColor = disabled ? palette.text.disabled : palette.text.primary

  return (
    <ProgramContainer
      onClick={disabled ? () => {} : () => onSelect(id)}
      disabled={disabled}
    >
      <CheckboxContainer checked={checked} disabled={disabled} />
      <TitleContainer>
        <TitleIconContainer checked={checked} disabled={disabled}>
          {icon}
          <Text variant='h5' marginBottom='1rem' color={textColor}>
            {title}
          </Text>
        </TitleIconContainer>
        <Text variant='body1' color={textColor}>
          {subtitle}
        </Text>
      </TitleContainer>
      {disabled && (
        <>
          <HelpIconContainer onClick={() => setIsModalOpen(true)}>
            <LightTooltip placement='left' title={disabledReason}>
              <HelpIcon sx={{ fontSize: '1.75rem' }} />
            </LightTooltip>
          </HelpIconContainer>
          <ModalContainer
            open={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            sidebarClose={() => setIsModalOpen(false)}
          >
            <ModelInnerContainer>
              <Text variant='action2'>{disabledReason}</Text>
            </ModelInnerContainer>
            <Close onClick={() => setIsModalOpen(false)}>
              <CloseIcon width={24} height={24} />
            </Close>
          </ModalContainer>
        </>
      )}
    </ProgramContainer>
  )
}

export default React.memo(ProgramSelectItem)
